/**
 * studio-audiogram.js — client-side audiogram video renderer.
 *
 * Turns a Studio project's narration + optional ambience into a WebM video
 * with an animated waveform, block-timed captions, and Rammy branding.
 *
 * Runs entirely in the browser:
 *   • Web Audio API decodes each block's stem to get its exact duration.
 *   • A MediaStreamDestination taps the audio into a stream.
 *   • A hidden <canvas> paints the frame each animationFrame; captureStream
 *     turns those frames into a video track.
 *   • MediaRecorder muxes canvas frames + audio into a single WebM blob.
 *
 * Aspect ratios: 1:1 (feeds), 9:16 (Reels / Shorts / Stories), 16:9 (YouTube).
 * Caption styles: classic (block of bold lines), karaoke (word highlight),
 * minimal (one quiet line, thin waveform).
 */

import { speechSegments, buildDuckEnvelope } from "./studio-mix";

// ── Palette ─────────────────────────────────────────────────────
const BG_TOP = "#F8F1E4";
const BG_BOT = "#F0E4CE";
const INK = "#211A15";
const INK_MUTED = "#6B5C4A";
const CYAN = "#178677";
const AMBER = "#E89A3C";
const BARS = 64;

export const ASPECTS = {
  "1:1":  { w: 1080, h: 1080, label: "Square 1:1" },
  "9:16": { w: 1080, h: 1920, label: "Vertical 9:16" },
  "16:9": { w: 1920, h: 1080, label: "Wide 16:9" },
};

export const CAPTION_STYLES = {
  classic: { label: "Classic" },
  karaoke: { label: "Karaoke" },
  minimal: { label: "Minimal" },
};

/** Decode a base64 audio clip to an AudioBuffer, silently returning null on
 *  failure so the caller can fall through to a text-only "poster" frame. */
async function decodeB64ToBuffer(ctx, b64) {
  if (!b64) return null;
  try {
    const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
    return await ctx.decodeAudioData(bytes.buffer.slice(0));
  } catch (e) {
    console.warn("audiogram decode failed", e);
    return null;
  }
}

/** Cumulative caption timings from per-block durations + the real gaps the
 *  server inserted after each paragraph. Words carry their own timings so the
 *  karaoke style can highlight in place. */
function buildCaptionTrack(blocks, stemDurations, gaps = []) {
  const track = [];
  let t = 0;
  for (let i = 0; i < blocks.length; i++) {
    const dur = stemDurations[i] || 0;
    const text = blocks[i]?.text || "";
    const words = text.split(/\s+/).filter(Boolean);
    const totalChars = words.reduce((s, w) => s + w.length, 0) || 1;
    let wt = t;
    const timedWords = words.map((w) => {
      const share = (w.length / totalChars) * dur;
      const entry = { word: w, start: wt, end: wt + share };
      wt += share;
      return entry;
    });
    track.push({ text, start: t, end: t + dur, words: timedWords });
    t += dur + (gaps[i] ?? 0.4);
  }
  return track;
}

/** Very small word-wrapper — measures on the canvas context. */
function wrapCaption(ctx, text, maxWidth, maxLines) {
  const words = text.split(/\s+/).filter(Boolean);
  const lines = [];
  let cur = "";
  for (const w of words) {
    const next = cur ? cur + " " + w : w;
    if (ctx.measureText(next).width > maxWidth && cur) {
      lines.push(cur);
      if (lines.length >= maxLines) return truncateLast(lines, maxLines);
      cur = w;
    } else {
      cur = next;
    }
  }
  if (cur) lines.push(cur);
  return truncateLast(lines, maxLines);
}

/** Same wrap, but keeping each word so karaoke can colour them individually. */
function wrapWords(ctx, words, maxWidth, maxLines) {
  const lines = [];
  let cur = [];
  let curText = "";
  for (const entry of words) {
    const next = curText ? curText + " " + entry.word : entry.word;
    if (ctx.measureText(next).width > maxWidth && cur.length) {
      lines.push(cur);
      if (lines.length >= maxLines) return lines;
      cur = [entry];
      curText = entry.word;
    } else {
      cur.push(entry);
      curText = next;
    }
  }
  if (cur.length) lines.push(cur);
  return lines.slice(0, maxLines);
}

function truncateLast(lines, maxLines) {
  if (lines.length <= maxLines) return lines;
  const kept = lines.slice(0, maxLines);
  kept[maxLines - 1] = kept[maxLines - 1].replace(/\s+\S+$/, "") + " …";
  return kept;
}

/** Load a data URL / blob URL / http URL into an Image element. */
function loadImage(src) {
  return new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/** Load a clip into a muted <video> we can draw onto the canvas.
 *  Muted because the scene's audio is the narration — a Sora clip's own
 *  soundtrack would fight it. */
function loadVideo(src) {
  return new Promise((resolve) => {
    if (!src) return resolve(null);
    const el = document.createElement("video");
    el.crossOrigin = "anonymous";
    el.muted = true;
    el.loop = true;
    el.playsInline = true;
    el.preload = "auto";
    el.onloadeddata = () => resolve(el);
    el.onerror = () => resolve(null);
    el.src = src;
  });
}

/** Resolve each scene's artwork once, up front. Returns an array aligned to
 *  `blocks`, holding `{ el, kind, motion }` or null where a scene has none. */
async function loadSceneVisuals(blocks) {
  return Promise.all(
    blocks.map(async (b) => {
      const v = b?.visual;
      if (!v?.url) return null;
      const el = v.kind === "video" ? await loadVideo(v.url) : await loadImage(v.url);
      if (!el) return null;
      return { el, kind: v.kind || "image", motion: v.motion || "drift" };
    }),
  );
}

/** Which scene is on screen at `t`, and how far through it we are (0..1).
 *  Falls back to the last scene during the trailing pause. */
function sceneAt(captionTrack, t) {
  for (let i = 0; i < captionTrack.length; i++) {
    const seg = captionTrack[i];
    const next = captionTrack[i + 1];
    const end = next ? next.start : seg.end;
    if (t < end) {
      const span = Math.max(0.001, end - seg.start);
      return { index: i, progress: Math.min(1, Math.max(0, (t - seg.start) / span)) };
    }
  }
  const last = captionTrack.length - 1;
  return { index: Math.max(0, last), progress: 1 };
}

/** Draw a visual to fill the frame, with a slow move so a still doesn't look
 *  like a frozen player. Cropped centre-out, never letterboxed. */
function drawSceneVisual(ctx, W, H, visual, progress) {
  const el = visual.el;
  const iw = el.naturalWidth || el.videoWidth || el.width;
  const ih = el.naturalHeight || el.videoHeight || el.height;
  if (!iw || !ih) return false;

  // Ken Burns: a 6% move over the life of the scene is enough to feel alive
  // and little enough that nobody reads it as a zoom.
  const AMP = 0.06;
  let zoom = 1 + AMP;
  let panX = 0;
  if (visual.motion === "zoom_in") zoom = 1 + AMP * progress;
  else if (visual.motion === "zoom_out") zoom = 1 + AMP * (1 - progress);
  else if (visual.motion === "still") zoom = 1;
  else panX = (progress - 0.5) * AMP; // drift

  const scale = Math.max(W / iw, H / ih) * zoom;
  const dw = iw * scale;
  const dh = ih * scale;
  ctx.drawImage(el, (W - dw) / 2 + panX * W, (H - dh) / 2, dw, dh);
  return true;
}

/** Paint one frame of the audiogram. Called on every requestAnimationFrame. */
function paintFrame({
  canvas, analyser, dataArr, captionTrack, currentTime, title,
  totalDuration, coverImage, captionStyle = "classic", brand = {}, logoImage = null,
  sceneVisuals = null,
}) {
  const ctx = canvas.getContext("2d");
  const W = canvas.width;
  const H = canvas.height;
  const pad = Math.round(W * 0.066);
  const minimal = captionStyle === "minimal";

  // ── Background ──
  // A scene's own artwork wins over the project cover: this is what turns a
  // Studio export from a waveform into something postable.
  let sceneDrawn = false;
  if (sceneVisuals) {
    const { index, progress } = sceneAt(captionTrack, currentTime);
    const visual = sceneVisuals[index];
    if (visual) sceneDrawn = drawSceneVisual(ctx, W, H, visual, progress);
  }
  if (sceneDrawn) {
    // Scrim only where type sits, so the picture stays the hero.
    const scrim = ctx.createLinearGradient(0, 0, 0, H);
    scrim.addColorStop(0, "rgba(15,10,6,0.42)");
    scrim.addColorStop(0.42, "rgba(15,10,6,0.10)");
    scrim.addColorStop(1, "rgba(15,10,6,0.66)");
    ctx.fillStyle = scrim;
    ctx.fillRect(0, 0, W, H);
  } else if (coverImage) {
    const iw = coverImage.naturalWidth || coverImage.width;
    const ih = coverImage.naturalHeight || coverImage.height;
    const scale = Math.max(W / iw, H / ih);
    const dw = iw * scale, dh = ih * scale;
    ctx.drawImage(coverImage, (W - dw) / 2, (H - dh) / 2, dw, dh);
    const scrim = ctx.createLinearGradient(0, 0, 0, H);
    scrim.addColorStop(0, "rgba(15,10,6,0.32)");
    scrim.addColorStop(0.5, "rgba(15,10,6,0.18)");
    scrim.addColorStop(1, "rgba(15,10,6,0.55)");
    ctx.fillStyle = scrim;
    ctx.fillRect(0, 0, W, H);
  } else {
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, BG_TOP);
    g.addColorStop(1, BG_BOT);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    const halo = ctx.createRadialGradient(W / 2, H * 0.32, 40, W / 2, H * 0.32, Math.max(W, H) * 0.55);
    halo.addColorStop(0, "rgba(232,154,60,0.18)");
    halo.addColorStop(1, "rgba(232,154,60,0)");
    ctx.fillStyle = halo;
    ctx.fillRect(0, 0, W, H);
  }

  const useLightInk = !!coverImage || sceneDrawn;
  const ink = useLightInk ? "#F5EDE0" : INK;
  const inkMuted = useLightInk ? "rgba(245,237,224,0.72)" : INK_MUTED;
  const cyan = useLightInk ? "#7CE7D2" : CYAN;
  const accent = brand.accent || AMBER;
  const captionColor = brand.caption_color || null;
  const handle = brand.handle || "rammy.ai";
  const wordmark = brand.wordmark || "Rammy Studio";

  // ── Branding row ──
  const brandY = pad + 18;
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";
  if (!minimal) {
    if (logoImage) {
      const lh = 40;
      const lw = Math.min(W * 0.42, lh * ((logoImage.naturalWidth || 1) / (logoImage.naturalHeight || 1)));
      ctx.drawImage(logoImage, pad, brandY - lh / 2, lw, lh);
      if (brand.show_rammy_mark !== false) {
        ctx.fillStyle = inkMuted;
        ctx.font = "500 15px 'Manrope', system-ui, sans-serif";
        ctx.fillText("with Rammy", pad + lw + 12, brandY + 1);
      }
    } else {
      ctx.fillStyle = accent;
      ctx.beginPath();
      ctx.arc(pad + 12, brandY, 12, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = ink;
      ctx.font = "600 26px 'Manrope', system-ui, sans-serif";
      ctx.fillText(wordmark, pad + 36, brandY);
    }
  }
  ctx.fillStyle = inkMuted;
  ctx.font = "500 18px 'Manrope', system-ui, sans-serif";
  const dm = Math.floor(totalDuration / 60);
  const ds = Math.round(totalDuration % 60);
  ctx.textAlign = "right";
  ctx.fillText(`${dm}:${String(ds).padStart(2, "0")}`, W - pad, brandY);
  ctx.textAlign = "left";

  // ── Title ──
  if (title && !minimal && !sceneDrawn) {
    ctx.fillStyle = ink;
    ctx.font = `600 ${Math.round(W * 0.041)}px 'Manrope', system-ui, sans-serif`;
    const t = title.length > 42 ? title.slice(0, 40) + "…" : title;
    ctx.fillText(t, pad, brandY + Math.round(H * 0.085));
  }

  // ── Waveform ──
  // Skipped over scene artwork — bars across someone's face is exactly the
  // "AI slop" look we're avoiding. The progress bar stays; it's useful.
  const bandW = W - pad * 2;
  if (!sceneDrawn) {
    analyser.getByteFrequencyData(dataArr);
    const centerY = H * (minimal ? 0.42 : 0.46);
    const step = bandW / BARS;
    const barW = step * (minimal ? 0.3 : 0.55);
    const maxBar = H * (minimal ? 0.12 : 0.3);
    for (let i = 0; i < BARS; i++) {
      const bucketSize = Math.floor(dataArr.length / BARS);
      let sum = 0;
      for (let j = 0; j < bucketSize; j++) sum += dataArr[i * bucketSize + j] || 0;
      const v = sum / bucketSize / 255;
      const h = Math.max(4, v * maxBar);
      const x = pad + i * step + (step - barW) / 2;
      const edge = i / BARS;
      const alpha = 0.35 + 0.65 * Math.sin(Math.PI * edge);
      ctx.globalAlpha = alpha;
      ctx.fillStyle = accent;
      ctx.fillRect(x, centerY - h / 2, barW, h);
    }
    ctx.globalAlpha = 1;
  }

  // ── Progress bar ──
  const pbY = sceneDrawn ? H - pad - 34 : H * (minimal ? 0.53 : 0.6);
  ctx.fillStyle = useLightInk ? "rgba(245,237,224,0.18)" : "rgba(33,26,21,0.08)";
  ctx.fillRect(pad, pbY, bandW, 4);
  const pbPct = Math.min(1, currentTime / Math.max(totalDuration, 0.001));
  ctx.fillStyle = brand.accent ? accent : cyan;
  ctx.fillRect(pad, pbY, bandW * pbPct, 4);

  // ── Captions ──
  const active = captionTrack.find(c => currentTime >= c.start && currentTime < c.end);
  if (active) {
    const capSize = Math.round(W * (minimal ? 0.026 : 0.034));
    const lineH = Math.round(capSize * 1.5);
    ctx.font = `600 ${capSize}px 'Manrope', system-ui, sans-serif`;
    const maxW = W - pad * 2.4;
    const maxLines = H > W ? 6 : 4;
    // Over artwork, captions sit low like a Reel; on the plain background they
    // keep their old position under the waveform.
    let y = sceneDrawn
      ? H * (H > W ? 0.74 : 0.72)
      : H * (minimal ? 0.62 : 0.7);

    if (sceneDrawn) {
      // A soft shadow is what keeps type legible on a photo.
      ctx.shadowColor = "rgba(0,0,0,0.55)";
      ctx.shadowBlur = Math.round(capSize * 0.5);
      ctx.shadowOffsetY = 2;
    }
    if (captionStyle === "karaoke") {
      const lines = wrapWords(ctx, active.words || [], maxW, maxLines);
      for (const line of lines) {
        let x = pad + 8;
        for (const entry of line) {
          const spoken = currentTime >= entry.start;
          const isCurrent = currentTime >= entry.start && currentTime < entry.end;
          ctx.fillStyle = isCurrent ? accent : (spoken ? (captionColor || ink) : inkMuted);
          ctx.fillText(entry.word, x, y);
          x += ctx.measureText(entry.word + " ").width;
        }
        y += lineH;
      }
    } else {
      ctx.fillStyle = minimal ? inkMuted : (captionColor || ink);
      const lines = wrapCaption(ctx, active.text, maxW, maxLines);
      for (const line of lines) {
        ctx.fillText(line, pad + 8, y);
        y += lineH;
      }
    }
    ctx.shadowColor = "transparent";
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;
  }

  // ── Footer ──
  ctx.fillStyle = inkMuted;
  ctx.font = "500 20px 'Manrope', system-ui, sans-serif";
  ctx.fillText(handle, pad, H - pad);
  if (handle !== "rammy.ai" && brand.show_rammy_mark !== false) {
    ctx.textAlign = "right";
    ctx.font = "500 16px 'Manrope', system-ui, sans-serif";
    ctx.fillText("made with Rammy", W - pad, H - pad);
    ctx.textAlign = "left";
  }
}

/**
 * Render a Studio project as an audiogram video.
 *
 * @param {object} opts
 * @param {string} opts.narrationB64  — merged narration MP3 (from render response)
 * @param {string} [opts.ambienceB64] — optional ambient loop
 * @param {number} [opts.ambienceVolume=0.18]
 * @param {Array}  opts.stems         — [{ audio_b64 }, ...] one per block
 * @param {Array}  opts.blocks        — [{ text }, ...] parallel to stems
 * @param {Array}  [opts.gaps]        — real silence in seconds after each stem
 * @param {string} [opts.aspect="1:1"]
 * @param {string} [opts.captionStyle="classic"]
 * @param {(pct: number, phase: string) => void} [opts.onProgress]
 * @returns {Promise<Blob>} WebM video blob
 */
export async function renderAudiogram({
  narrationB64, ambienceB64, ambienceVolume = 0.18,
  musicBuffer = null, musicVolume = 0.22, musicDuck = 0.7,
  stems = [], blocks = [], gaps = [], title = "", coverDataUrl = null,
  aspect = "1:1", captionStyle = "classic", brand = {}, logoUrl = null,
  withScenes = true,
  onProgress = () => {},
}) {
  onProgress(0.02, "decoding");
  const AC = window.AudioContext || window.webkitAudioContext;
  const ctx = new AC({ sampleRate: 44100 });

  const coverPromise = loadImage(coverDataUrl);
  const logoPromise = loadImage(logoUrl);

  const narrationBuf = await decodeB64ToBuffer(ctx, narrationB64);
  if (!narrationBuf) throw new Error("Could not decode narration audio.");
  const stemDurations = [];
  for (const s of stems) {
    const b = await decodeB64ToBuffer(ctx, s.audio_b64);
    stemDurations.push(b ? b.duration : 0);
  }
  const captionTrack = buildCaptionTrack(blocks, stemDurations, gaps);
  const totalDuration = narrationBuf.duration;
  const ambienceBuf = ambienceB64 ? await decodeB64ToBuffer(ctx, ambienceB64) : null;
  let musicBuf = null;
  if (musicBuffer && musicVolume > 0) {
    try {
      musicBuf = await ctx.decodeAudioData(musicBuffer.slice(0));
    } catch (e) {
      console.warn("music decode failed", e);
    }
  }
  const coverImage = await coverPromise;
  const logoImage = await logoPromise;

  // Scene artwork. `null` throughout means this project is audio-only and we
  // fall back to the waveform look.
  const loadedVisuals = withScenes && blocks.some(b => b?.visual?.url)
    ? await loadSceneVisuals(blocks)
    : null;
  const sceneVisuals = loadedVisuals?.some(Boolean) ? loadedVisuals : null;

  onProgress(0.12, "wiring");

  // ── Audio graph: narration + ambience → analyser → destination ──
  const dest = ctx.createMediaStreamDestination();
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 512;
  const dataArr = new Uint8Array(analyser.frequencyBinCount);

  const narrationSrc = ctx.createBufferSource();
  narrationSrc.buffer = narrationBuf;
  const narrationGain = ctx.createGain();
  narrationGain.gain.value = 1.0;
  narrationSrc.connect(narrationGain);
  narrationGain.connect(analyser);
  narrationGain.connect(dest);

  let ambienceSrc = null;
  if (ambienceBuf) {
    ambienceSrc = ctx.createBufferSource();
    ambienceSrc.buffer = ambienceBuf;
    ambienceSrc.loop = true;
    const ambGain = ctx.createGain();
    ambGain.gain.value = Math.max(0, Math.min(1, ambienceVolume));
    ambienceSrc.connect(ambGain);
    ambGain.connect(analyser);
    ambGain.connect(dest);
  }

  // Music bed: looped, and ducked under the voice using the same envelope the
  // exported audio uses, so playback and download agree.
  let musicSrcs = [];
  let musicGain = null;
  let musicEnvelope = null;
  if (musicBuf) {
    musicGain = ctx.createGain();
    musicGain.gain.value = 0;
    musicGain.connect(analyser);
    musicGain.connect(dest);
    const segments = await speechSegments(ctx, stems, gaps);
    musicEnvelope = buildDuckEnvelope({
      total: totalDuration, segments, full: musicVolume, duck: musicDuck,
    });
    let cursor = 0;
    while (cursor < totalDuration) {
      const src = ctx.createBufferSource();
      src.buffer = musicBuf;
      src.connect(musicGain);
      musicSrcs.push({ src, at: cursor });
      cursor += musicBuf.duration;
    }
  }

  // ── Canvas + capture ──
  const dims = ASPECTS[aspect] || ASPECTS["1:1"];
  const canvas = document.createElement("canvas");
  canvas.width = dims.w;
  canvas.height = dims.h;
  const frameArgs = { canvas, analyser, dataArr, captionTrack, title, totalDuration, coverImage, captionStyle, brand, logoImage, sceneVisuals };
  paintFrame({ ...frameArgs, currentTime: 0 });
  const videoStream = canvas.captureStream(30);
  const audioTrack = dest.stream.getAudioTracks()[0];
  const combined = new MediaStream([...videoStream.getVideoTracks(), audioTrack]);

  // ── Recorder ──
  const mimeCandidates = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
  ];
  const mime = mimeCandidates.find(m => window.MediaRecorder?.isTypeSupported?.(m)) || "video/webm";
  const recorder = new MediaRecorder(combined, { mimeType: mime, videoBitsPerSecond: 3_500_000 });
  const chunks = [];
  recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };

  const donePromise = new Promise((resolve) => {
    recorder.onstop = () => resolve(new Blob(chunks, { type: mime }));
  });

  onProgress(0.18, "recording");
  recorder.start(200);
  const t0 = ctx.currentTime + 0.05;
  narrationSrc.start(t0);
  if (ambienceSrc) ambienceSrc.start(t0);
  if (musicGain && musicEnvelope) {
    musicSrcs.forEach(({ src, at }) => src.start(t0 + at));
    try {
      musicGain.gain.setValueCurveAtTime(musicEnvelope, t0, totalDuration);
    } catch (e) {
      musicGain.gain.value = musicVolume * (1 - musicDuck);
    }
  }

  let raf = 0;
  // Video scenes have to be playing for drawImage to yield anything but a
  // black rectangle. Start each clip when its scene comes up, pause it after.
  let playingIndex = -1;
  const tick = () => {
    const currentTime = Math.max(0, ctx.currentTime - t0);
    if (sceneVisuals) {
      const { index } = sceneAt(captionTrack, currentTime);
      if (index !== playingIndex) {
        const prev = sceneVisuals[playingIndex];
        if (prev?.kind === "video") { try { prev.el.pause(); } catch (_) { /* ignore */ } }
        const next = sceneVisuals[index];
        if (next?.kind === "video") {
          try { next.el.currentTime = 0; next.el.play(); } catch (_) { /* ignore */ }
        }
        playingIndex = index;
      }
    }
    paintFrame({ ...frameArgs, currentTime });
    if (currentTime < totalDuration + 0.1) {
      onProgress(0.18 + 0.75 * (currentTime / totalDuration), "recording");
      raf = requestAnimationFrame(tick);
    }
  };
  raf = requestAnimationFrame(tick);

  narrationSrc.onended = () => {
    setTimeout(() => {
      cancelAnimationFrame(raf);
      try { recorder.stop(); } catch (_) { /* ignore */ }
      try { if (ambienceSrc) ambienceSrc.stop(); } catch (_) { /* ignore */ }
      (sceneVisuals || []).forEach(v => {
        if (v?.kind === "video") { try { v.el.pause(); } catch (_) { /* ignore */ } }
      });
      musicSrcs.forEach(({ src }) => { try { src.stop(); } catch (_) { /* ignore */ } });
      try { ctx.close(); } catch (_) { /* ignore */ }
    }, 250);
  };

  const blob = await donePromise;
  onProgress(1, "done");
  return blob;
}
